/**
 * W65 — OpportunityLines
 * Pricing line items table with edit-in-place, add line, subtotal + header discount.
 */
import { useState } from "react";
import { getSession } from "@/lib/session";

const CATEGORIES = [
  { value: "platform_subscription", label: "Platform Subscription" },
  { value: "bootcamp_training",     label: "Bootcamp / Training" },
  { value: "professional_services", label: "Professional Services" },
  { value: "implementation_setup",  label: "Implementation Setup" },
];

const RECURRENCES = [
  { value: "one_time", label: "One-time" },
  { value: "monthly",  label: "Monthly" },
  { value: "annual",   label: "Annual" },
];

function fmtCurrency(n: number): string {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(n);
}

interface LineItem {
  id: string;
  product_category: string;
  description: string;
  unit_label: string;
  quantity: number;
  unit_price: number;
  line_discount_pct: number;
  line_total: number;
  recurrence: string;
  notes?: string | null;
  sort_order: number;
}

interface OpportunityLinesProps {
  opportunityId: string;
  lines: LineItem[];
  subtotalDiscountPct: number;
  onLinesChanged: () => void;
}

const BLANK_LINE = {
  product_category: "platform_subscription",
  description: "",
  unit_label: "unit",
  quantity: 1,
  unit_price: 0,
  line_discount_pct: 0,
  recurrence: "one_time",
  notes: "",
};

export function OpportunityLines({
  opportunityId,
  lines,
  subtotalDiscountPct,
  onLinesChanged,
}: OpportunityLinesProps) {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState<Partial<LineItem>>({});
  const [addingNew, setAddingNew] = useState(false);
  const [newDraft, setNewDraft] = useState({ ...BLANK_LINE });
  const [headerDiscount, setHeaderDiscount] = useState(subtotalDiscountPct);
  const [headerDiscountEditing, setHeaderDiscountEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const session = getSession();
  const token = session ? (session as any).access_token ?? (session as any).token ?? "" : "";

  const authHeader = token ? { Authorization: `Bearer ${token}` } : {};

  async function saveEdit(lineId: string) {
    setSaving(true);
    setError(null);
    try {
      const res = await fetch(`/api/opportunities/${opportunityId}/lines/${lineId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", ...authHeader },
        body: JSON.stringify(editDraft),
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.error ?? "Failed to save line");
      }
      setEditingId(null);
      onLinesChanged();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  }

  async function deleteLine(lineId: string) {
    if (!confirm("Remove this line item?")) return;
    setSaving(true);
    setError(null);
    try {
      const res = await fetch(`/api/opportunities/${opportunityId}/lines/${lineId}`, {
        method: "DELETE",
        headers: { ...authHeader },
      });
      if (!res.ok) throw new Error("Failed to delete line");
      onLinesChanged();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  }

  async function addLine() {
    if (!newDraft.description.trim()) { setError("Description required"); return; }
    setSaving(true);
    setError(null);
    try {
      const res = await fetch(`/api/opportunities/${opportunityId}/lines`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeader },
        body: JSON.stringify(newDraft),
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.error ?? "Failed to add line");
      }
      setAddingNew(false);
      setNewDraft({ ...BLANK_LINE });
      onLinesChanged();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  }

  async function saveHeaderDiscount() {
    setSaving(true);
    setError(null);
    try {
      const res = await fetch(`/api/opportunities/${opportunityId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", ...authHeader },
        body: JSON.stringify({ subtotal_discount_pct: headerDiscount }),
      });
      if (!res.ok) throw new Error("Failed to save deal discount");
      setHeaderDiscountEditing(false);
      onLinesChanged();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  }

  const grossTotal = lines.reduce((s, l) => s + Number(l.line_total), 0);
  const netTotal = grossTotal * (1 - (Number(headerDiscount) || 0) / 100);

  const thStyle: React.CSSProperties = {
    fontSize: 11,
    fontWeight: 700,
    color: "#6B7280",
    textTransform: "uppercase",
    letterSpacing: "0.08em",
    padding: "6px 10px",
    textAlign: "left",
    borderBottom: "1px solid #E5E5E7",
    background: "#F5F5F4",
    whiteSpace: "nowrap",
  };
  const tdStyle: React.CSSProperties = {
    fontSize: 13,
    color: "#2A2A2E",
    padding: "8px 10px",
    borderBottom: "1px solid #EFEFEE",
    verticalAlign: "middle",
  };
  const inputSt: React.CSSProperties = {
    border: "1px solid #C9C9CC",
    borderRadius: 4,
    padding: "4px 7px",
    fontSize: 12,
    width: "100%",
    boxSizing: "border-box",
  };

  return (
    <div style={{ background: "#FFFFFF", borderRadius: 8, border: "1px solid #E5E5E7", overflow: "hidden" }}>
      <div style={{ padding: "12px 16px 8px", borderBottom: "1px solid #E5E5E7", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: "#0B0B0F" }}>Pricing Lines</h3>
        {!addingNew && (
          <button
            onClick={() => { setAddingNew(true); setError(null); }}
            style={{ fontSize: 12, fontWeight: 600, color: "#0B0B0F", background: "none", border: "none", cursor: "pointer", padding: "2px 6px" }}
          >
            + Add line
          </button>
        )}
      </div>

      {error && (
        <div style={{ background: "#FEE2E2", color: "#991B1B", fontSize: 12, padding: "8px 16px" }}>{error}</div>
      )}

      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 640 }}>
          <thead>
            <tr>
              <th style={thStyle}>Category</th>
              <th style={thStyle}>Description</th>
              <th style={{ ...thStyle, textAlign: "right" }}>Qty</th>
              <th style={{ ...thStyle, textAlign: "right" }}>Unit Price</th>
              <th style={{ ...thStyle, textAlign: "right" }}>Disc %</th>
              <th style={{ ...thStyle, textAlign: "right" }}>Line Total</th>
              <th style={{ ...thStyle, textAlign: "center" }}>Recurrence</th>
              <th style={{ ...thStyle, width: 60 }}></th>
            </tr>
          </thead>
          <tbody>
            {lines.map((line) =>
              editingId === line.id ? (
                <tr key={line.id} style={{ background: "#FAFAF7" }}>
                  <td style={tdStyle}>
                    <select
                      style={inputSt}
                      value={editDraft.product_category ?? line.product_category}
                      onChange={(e) => setEditDraft((d) => ({ ...d, product_category: e.target.value }))}
                    >
                      {CATEGORIES.map((c) => (
                        <option key={c.value} value={c.value}>{c.label}</option>
                      ))}
                    </select>
                  </td>
                  <td style={tdStyle}>
                    <input
                      style={inputSt}
                      value={editDraft.description ?? line.description}
                      onChange={(e) => setEditDraft((d) => ({ ...d, description: e.target.value }))}
                    />
                  </td>
                  <td style={{ ...tdStyle, textAlign: "right" }}>
                    <input
                      style={{ ...inputSt, width: 60, textAlign: "right" }}
                      type="number"
                      min="0"
                      value={editDraft.quantity ?? line.quantity}
                      onChange={(e) => setEditDraft((d) => ({ ...d, quantity: Number(e.target.value) }))}
                    />
                  </td>
                  <td style={{ ...tdStyle, textAlign: "right" }}>
                    <input
                      style={{ ...inputSt, width: 90, textAlign: "right" }}
                      type="number"
                      min="0"
                      value={editDraft.unit_price ?? line.unit_price}
                      onChange={(e) => setEditDraft((d) => ({ ...d, unit_price: Number(e.target.value) }))}
                    />
                  </td>
                  <td style={{ ...tdStyle, textAlign: "right" }}>
                    <input
                      style={{ ...inputSt, width: 50, textAlign: "right" }}
                      type="number"
                      min="0"
                      max="100"
                      value={editDraft.line_discount_pct ?? line.line_discount_pct}
                      onChange={(e) => setEditDraft((d) => ({ ...d, line_discount_pct: Number(e.target.value) }))}
                    />
                  </td>
                  <td style={{ ...tdStyle, textAlign: "right", color: "#6B7280", fontStyle: "italic" }}>
                    recalculating…
                  </td>
                  <td style={{ ...tdStyle, textAlign: "center" }}>
                    <select
                      style={{ ...inputSt, width: 90 }}
                      value={editDraft.recurrence ?? line.recurrence}
                      onChange={(e) => setEditDraft((d) => ({ ...d, recurrence: e.target.value }))}
                    >
                      {RECURRENCES.map((r) => (
                        <option key={r.value} value={r.value}>{r.label}</option>
                      ))}
                    </select>
                  </td>
                  <td style={{ ...tdStyle, textAlign: "center", whiteSpace: "nowrap" }}>
                    <button
                      onClick={() => saveEdit(line.id)}
                      disabled={saving}
                      style={{ fontSize: 11, color: "#0B0B0F", background: "none", border: "none", cursor: "pointer", marginRight: 4 }}
                    >
                      Save
                    </button>
                    <button
                      onClick={() => setEditingId(null)}
                      style={{ fontSize: 11, color: "#6B7280", background: "none", border: "none", cursor: "pointer" }}
                    >
                      Cancel
                    </button>
                  </td>
                </tr>
              ) : (
                <tr key={line.id}>
                  <td style={tdStyle}>
                    <span style={{ fontSize: 11, background: "#EFEFEE", color: "#3F3F44", padding: "2px 7px", borderRadius: 4, fontWeight: 600 }}>
                      {CATEGORIES.find((c) => c.value === line.product_category)?.label ?? line.product_category}
                    </span>
                  </td>
                  <td style={{ ...tdStyle, maxWidth: 240 }}>
                    <div style={{ fontWeight: 500 }}>{line.description}</div>
                    {line.unit_label && line.unit_label !== "unit" && (
                      <div style={{ fontSize: 11, color: "#8E8E94" }}>per {line.unit_label}</div>
                    )}
                  </td>
                  <td style={{ ...tdStyle, textAlign: "right" }}>{line.quantity}</td>
                  <td style={{ ...tdStyle, textAlign: "right" }}>{fmtCurrency(Number(line.unit_price))}</td>
                  <td style={{ ...tdStyle, textAlign: "right", color: line.line_discount_pct > 0 ? "#DC2626" : "#C9C9CC" }}>
                    {line.line_discount_pct > 0 ? `${line.line_discount_pct}%` : "—"}
                  </td>
                  <td style={{ ...tdStyle, textAlign: "right", fontWeight: 700 }}>{fmtCurrency(Number(line.line_total))}</td>
                  <td style={{ ...tdStyle, textAlign: "center", fontSize: 11, color: "#6B7280" }}>
                    {RECURRENCES.find((r) => r.value === line.recurrence)?.label ?? line.recurrence}
                  </td>
                  <td style={{ ...tdStyle, textAlign: "center", whiteSpace: "nowrap" }}>
                    <button
                      onClick={() => { setEditingId(line.id); setEditDraft({}); }}
                      style={{ fontSize: 11, color: "#0B0B0F", background: "none", border: "none", cursor: "pointer", marginRight: 4 }}
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => deleteLine(line.id)}
                      style={{ fontSize: 11, color: "#DC2626", background: "none", border: "none", cursor: "pointer" }}
                    >
                      ✕
                    </button>
                  </td>
                </tr>
              )
            )}

            {/* Add new row */}
            {addingNew && (
              <tr style={{ background: "#F0F9FF" }}>
                <td style={tdStyle}>
                  <select
                    style={inputSt}
                    value={newDraft.product_category}
                    onChange={(e) => setNewDraft((d) => ({ ...d, product_category: e.target.value }))}
                  >
                    {CATEGORIES.map((c) => (
                      <option key={c.value} value={c.value}>{c.label}</option>
                    ))}
                  </select>
                </td>
                <td style={tdStyle}>
                  <input
                    style={inputSt}
                    placeholder="Description"
                    value={newDraft.description}
                    onChange={(e) => setNewDraft((d) => ({ ...d, description: e.target.value }))}
                    autoFocus
                  />
                </td>
                <td style={{ ...tdStyle, textAlign: "right" }}>
                  <input
                    style={{ ...inputSt, width: 60, textAlign: "right" }}
                    type="number"
                    min="0"
                    value={newDraft.quantity}
                    onChange={(e) => setNewDraft((d) => ({ ...d, quantity: Number(e.target.value) }))}
                  />
                </td>
                <td style={{ ...tdStyle, textAlign: "right" }}>
                  <input
                    style={{ ...inputSt, width: 90, textAlign: "right" }}
                    type="number"
                    min="0"
                    value={newDraft.unit_price}
                    onChange={(e) => setNewDraft((d) => ({ ...d, unit_price: Number(e.target.value) }))}
                  />
                </td>
                <td style={{ ...tdStyle, textAlign: "right" }}>
                  <input
                    style={{ ...inputSt, width: 50, textAlign: "right" }}
                    type="number"
                    min="0"
                    max="100"
                    value={newDraft.line_discount_pct}
                    onChange={(e) => setNewDraft((d) => ({ ...d, line_discount_pct: Number(e.target.value) }))}
                  />
                </td>
                <td style={{ ...tdStyle, textAlign: "right", color: "#8E8E94", fontStyle: "italic" }}>—</td>
                <td style={{ ...tdStyle, textAlign: "center" }}>
                  <select
                    style={{ ...inputSt, width: 90 }}
                    value={newDraft.recurrence}
                    onChange={(e) => setNewDraft((d) => ({ ...d, recurrence: e.target.value }))}
                  >
                    {RECURRENCES.map((r) => (
                      <option key={r.value} value={r.value}>{r.label}</option>
                    ))}
                  </select>
                </td>
                <td style={{ ...tdStyle, textAlign: "center", whiteSpace: "nowrap" }}>
                  <button
                    onClick={addLine}
                    disabled={saving}
                    style={{ fontSize: 11, color: "#16A34A", background: "none", border: "none", cursor: "pointer", marginRight: 4, fontWeight: 700 }}
                  >
                    Add
                  </button>
                  <button
                    onClick={() => { setAddingNew(false); setNewDraft({ ...BLANK_LINE }); }}
                    style={{ fontSize: 11, color: "#6B7280", background: "none", border: "none", cursor: "pointer" }}
                  >
                    Cancel
                  </button>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Footer totals */}
      <div
        style={{
          borderTop: "2px solid #E5E5E7",
          padding: "10px 16px",
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-end",
          gap: 6,
          background: "#FAFAF7",
        }}
      >
        <div style={{ display: "flex", gap: 24, alignItems: "center" }}>
          <span style={{ fontSize: 12, color: "#6B7280" }}>Subtotal</span>
          <span style={{ fontSize: 14, fontWeight: 600, color: "#2A2A2E", minWidth: 100, textAlign: "right" }}>
            {fmtCurrency(grossTotal)}
          </span>
        </div>
        <div style={{ display: "flex", gap: 16, alignItems: "center" }}>
          <span style={{ fontSize: 12, color: "#6B7280" }}>Deal Discount</span>
          {headerDiscountEditing ? (
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <input
                type="number"
                min="0"
                max="100"
                value={headerDiscount}
                onChange={(e) => setHeaderDiscount(Number(e.target.value))}
                style={{ width: 60, ...inputSt, textAlign: "right" }}
              />
              <span style={{ fontSize: 12, color: "#6B7280" }}>%</span>
              <button
                onClick={saveHeaderDiscount}
                disabled={saving}
                style={{ fontSize: 11, color: "#0B0B0F", background: "none", border: "none", cursor: "pointer" }}
              >
                Save
              </button>
              <button
                onClick={() => { setHeaderDiscountEditing(false); setHeaderDiscount(subtotalDiscountPct); }}
                style={{ fontSize: 11, color: "#6B7280", background: "none", border: "none", cursor: "pointer" }}
              >
                Cancel
              </button>
            </div>
          ) : (
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <span style={{ fontSize: 13, fontWeight: 600, color: headerDiscount > 0 ? "#DC2626" : "#C9C9CC", minWidth: 100, textAlign: "right" }}>
                {headerDiscount > 0 ? `${headerDiscount}%` : "None"}
              </span>
              <button
                onClick={() => setHeaderDiscountEditing(true)}
                style={{ fontSize: 11, color: "#0B0B0F", background: "none", border: "none", cursor: "pointer" }}
              >
                Edit
              </button>
            </div>
          )}
        </div>
        <div style={{ display: "flex", gap: 24, alignItems: "center", borderTop: "1px solid #E5E5E7", paddingTop: 6, marginTop: 2 }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: "#0B0B0F" }}>Net Total</span>
          <span style={{ fontSize: 18, fontWeight: 800, color: "#0B0B0F", minWidth: 100, textAlign: "right" }}>
            {fmtCurrency(netTotal)}
          </span>
        </div>
      </div>
    </div>
  );
}
